import { Box, SimpleGrid, Stack, Group, Badge, Progress, Avatar, RingProgress, Center } from '@mantine/core';
import {
  IconFolders,
  IconChecklist,
  IconUsers,
  IconClock,
  IconArrowRight,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockProjects, mockTasks } from '../../mockData';

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color
}: {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ElementType;
  color: string;
}) => (
  <Card>
    <Group justify="space-between" mb="xs">
      <Text size="sm" c="dimmed">{title}</Text>
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          backgroundColor: `var(--mantine-color-${color}-light)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
      </Box>
    </Group>
    <Text size="xl" fw={700}>{value}</Text>
    <Text size="xs" c="dimmed">{subtitle}</Text>
  </Card>
);

export default function PMDashboardPage() {
  const navigate = useNavigate();

  const activeProjects = mockProjects.filter(p => p.status === 'in-progress');
  const completedProjects = mockProjects.filter(p => p.status === 'completed');
  const pendingTasks = mockTasks.filter(t => t.status === 'todo' || t.status === 'in-progress');
  const completedTasks = mockTasks.filter(t => t.status === 'done');
  const uniqueMembers = new Set(mockProjects.flatMap(p => p.memberIds));

  const projectsByStatus = [
    { status: 'In Progress', count: mockProjects.filter(p => p.status === 'in-progress').length, color: 'blue' },
    { status: 'Planning', count: mockProjects.filter(p => p.status === 'planning').length, color: 'cyan' },
    { status: 'On Hold', count: mockProjects.filter(p => p.status === 'on-hold').length, color: 'yellow' },
    { status: 'Completed', count: mockProjects.filter(p => p.status === 'completed').length, color: 'green' },
  ];

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Project Management</Title>
          <Text c="dimmed" size="sm">Overview of all projects and tasks</Text>
        </Box>
        <Group>
          <Button variant="light" onClick={() => navigate('board')}>View Board</Button>
          <Button onClick={() => navigate('projects')}>View Projects</Button>
        </Group>
      </Group>

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Active Projects"
          value={activeProjects.length}
          subtitle={`${completedProjects.length} completed`}
          icon={IconFolders}
          color="blue"
        />
        <StatCard
          title="Pending Tasks"
          value={pendingTasks.length}
          subtitle={`${completedTasks.length} completed`}
          icon={IconChecklist}
          color="orange"
        />
        <StatCard
          title="Team Members"
          value={uniqueMembers.size}
          subtitle="Active contributors"
          icon={IconUsers}
          color="violet"
        />
        <StatCard
          title="Avg. Completion"
          value={`${Math.round(mockProjects.reduce((acc, p) => acc + p.progress, 0) / mockProjects.length)}%`}
          subtitle="Across all projects"
          icon={IconClock}
          color="green"
        />
      </SimpleGrid>

      {/* Projects and Stats */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Project Progress */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Project Progress</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('projects')}
            >
              View All
            </Button>
          </Group>
          <Stack gap="md">
            {activeProjects.slice(0, 5).map((project) => (
              <Box key={project.id}>
                <Group justify="space-between" mb={4}>
                  <Text size="sm" fw={500}>{project.name}</Text>
                  <Badge size="xs" color={
                    project.priority === 'urgent' ? 'red' :
                    project.priority === 'high' ? 'orange' :
                    project.priority === 'medium' ? 'yellow' : 'gray'
                  }>
                    {project.priority}
                  </Badge>
                </Group>
                <Group gap="xs" mb={4}>
                  <Progress value={project.progress} size="lg" radius="xl" style={{ flex: 1 }} />
                  <Text size="xs" w={35} ta="right">{project.progress}%</Text>
                </Group>
                <Text size="xs" c="dimmed">
                  Due: {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(project.dueDate)}
                </Text>
              </Box>
            ))}
          </Stack>
        </Card>

        {/* Projects by Status */}
        <Card>
          <Text fw={600} mb="md">Projects by Status</Text>
          <Group justify="center" mb="lg">
            <RingProgress
              size={180}
              thickness={20}
              roundCaps
              sections={projectsByStatus.map(s => ({
                value: (s.count / mockProjects.length) * 100,
                color: s.color,
              }))}
              label={
                <Center>
                  <Box ta="center">
                    <Text size="xl" fw={700}>{mockProjects.length}</Text>
                    <Text size="xs" c="dimmed">Total Projects</Text>
                  </Box>
                </Center>
              }
            />
          </Group>
          <Stack gap="xs">
            {projectsByStatus.map(item => (
              <Group key={item.status} justify="space-between">
                <Group gap="sm">
                  <Box w={12} h={12} style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${item.color}-filled)` }} />
                  <Text size="sm">{item.status}</Text>
                </Group>
                <Text size="sm" fw={500}>{item.count}</Text>
              </Group>
            ))}
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Recent Tasks */}
      <Card>
        <Group justify="space-between" mb="md">
          <Text fw={600}>Recent Tasks</Text>
          <Button
            variant="subtle"
            size="xs"
            rightSection={<IconArrowRight size={14} />}
            onClick={() => navigate('board')}
          >
            View Board
          </Button>
        </Group>
        <Stack gap="sm">
          {mockTasks.slice(0, 6).map((task) => {
            const project = mockProjects.find(p => p.id === task.projectId);
            return (
              <Group key={task.id} justify="space-between" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                <Group gap="sm">
                  <Box>
                    <Text size="sm" fw={500}>{task.title}</Text>
                    <Text size="xs" c="dimmed">{project?.name}</Text>
                  </Box>
                </Group>
                <Group gap="sm">
                  <Badge size="sm" color={
                    task.status === 'done' ? 'green' :
                    task.status === 'in-progress' ? 'blue' :
                    task.status === 'review' ? 'cyan' :
                    task.status === 'blocked' ? 'red' : 'gray'
                  }>
                    {task.status}
                  </Badge>
                  {task.assigneeId && (
                    <Avatar size="sm" radius="xl" color="blue">
                      {task.assigneeId.slice(-2)}
                    </Avatar>
                  )}
                </Group>
              </Group>
            );
          })}
        </Stack>
      </Card>
    </Stack>
  );
}
